import React, { useState, useEffect } from 'react';
import ReactPlayer from 'react-player/lazy';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { IProps } from '@/elements/Video/Video.interface';
import '@/elements/Video/Video.styles.css';

/**
 * YouTube requires the embedding page's origin on the iframe request.
 * A page-level Referrer-Policy of same-origin strips that header and
 * YouTube then rejects the player (error 153). Set the policy on the
 * iframe itself, before src is requested. react-player's YouTube API
 * creates the iframe internally and cannot set this attribute in time.
 */
const YOUTUBE_REFERRER_POLICY = 'strict-origin-when-cross-origin';

const YOUTUBE_ID_PATTERN =
  /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:embed\/|shorts\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/;

function youTubeEmbedUrl(
  videoId: string,
  options: {
    controls: boolean;
    autoplay: boolean;
    loop: boolean;
    muted: boolean;
    playerVars?: Record<string, unknown>;
  },
): string {
  const params = new URLSearchParams({
    controls: options.controls ? '1' : '0',
    rel: '0',
    modestbranding: '1',
    iv_load_policy: '3',
    autoplay: options.autoplay ? '1' : '0',
  });

  if (options.loop) {
    params.set('loop', '1');
    params.set('playlist', videoId);
  }
  if (options.muted) params.set('mute', '1');

  if (options.playerVars) {
    for (const [key, value] of Object.entries(options.playerVars)) {
      if (value === undefined || value === null) continue;
      params.set(key, String(value));
    }
  }

  return `https://www.youtube-nocookie.com/embed/${videoId}?${params}`;
}

/**
 * @element Video
 * @description Responsive video player. YouTube uses a privacy-enhanced iframe
 * that sends the site origin. Vimeo and file URLs use react-player.
 */
const Video: React.FC<IProps> = ({
  url,
  className,
  playing = false, // Initial playing state from props
  controls = true,
  light = false,
  width = '100%',
  height = '100%',
  loop = false,
  muted = false,
  volume = 0.8,
  config,
  aspectRatio = '16/9',
  ...rest
}) => {
  const [internalPlaying, setInternalPlaying] = useState(playing);

  // Sync internal state if the playing prop changes externally
  useEffect(() => {
    setInternalPlaying(playing);
  }, [playing]);

  const handlePreviewClick = () => {
    setInternalPlaying(true);
  };

  const [aspectWidth, aspectHeight] = aspectRatio.split('/').map(Number);
  const paddingTop =
    aspectHeight && aspectWidth
      ? `${(aspectHeight / aspectWidth) * 100}%`
      : '56.25%';

  // Custom Play Icon Element
  const customPlayIcon = (
    <div className="video__play-button" role="button" tabIndex={0}>
      <FontAwesomeIcon icon={faPlay} />
    </div>
  );

  // Plain iframe for every browser. react-player's YouTube API injects
  // enablejsapi=1 (Safari ITP error 153) and never sets referrerpolicy.
  const youTubeVideoId = url?.match(YOUTUBE_ID_PATTERN)?.[1];

  if (youTubeVideoId) {
    const embedUrl = youTubeEmbedUrl(youTubeVideoId, {
      controls,
      autoplay: internalPlaying,
      loop,
      muted,
      playerVars: config?.youtube?.playerVars,
    });

    // Light mode: show a clickable thumbnail until the user triggers playback
    if (light && !internalPlaying) {
      const thumbnailUrl =
        typeof light === 'string'
          ? light
          : `https://img.youtube.com/vi/${youTubeVideoId}/maxresdefault.jpg`;
      return (
        <div
          className={clsx('video', className)}
          style={{ paddingTop }}
          {...rest}
        >
          <div
            className="video__player react-player__preview"
            style={{ backgroundImage: `url(${thumbnailUrl})` }}
            role="button"
            tabIndex={0}
            onClick={handlePreviewClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handlePreviewClick();
            }}
            aria-label="Play video"
          >
            <div className="react-player__shadow">{customPlayIcon}</div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={clsx('video', className)}
        style={{ paddingTop }}
        {...rest}
      >
        <iframe
          className="video__player"
          src={embedUrl}
          allow="accelerometer *; clipboard-write *; encrypted-media *; gyroscope *; picture-in-picture *; web-share *; autoplay *; fullscreen *"
          allowFullScreen
          referrerPolicy={YOUTUBE_REFERRER_POLICY}
          title="YouTube video"
        />
      </div>
    );
  }

  // Vimeo and file URLs. YouTube is handled above.
  const mergedConfig: typeof config = {
    ...config,
    youtube: {
      ...config?.youtube,
      embedOptions: {
        ...config?.youtube?.embedOptions,
        host: 'https://www.youtube-nocookie.com',
      },
    },
  };

  return (
    <div className={clsx('video', className)} style={{ paddingTop }} {...rest}>
      {url && (
        <ReactPlayer
          className="video__player"
          url={url}
          playing={internalPlaying} // Use internal state
          controls={controls}
          light={light} // Keep passing the original light prop
          playIcon={customPlayIcon}
          onClickPreview={handlePreviewClick} // Handle the click
          // Sync state back if playback is controlled externally (e.g., pause button)
          onPlay={() => {
            if (!internalPlaying) setInternalPlaying(true);
          }}
          onPause={() => {
            if (internalPlaying) setInternalPlaying(false);
          }}
          width={width}
          height={height}
          loop={loop}
          muted={muted}
          volume={volume}
          config={mergedConfig}
        />
      )}
    </div>
  );
};

export default Video;
